import React, { useContext, useEffect, useState } from 'react';
import api from '../api';
import { SelectedCompanyContext } from '../contexts/SelectedCompanyContext';
import { format } from 'date-fns';
import { 
  AnimatedBackground, 
  ModernCard, 
  ModernBadge, 
  ModernTextarea,
  ModernSelect,
  ModernButton,
  getStatusVariant
} from './ui/modern';
import { getFallbackTemplates, useCompanyTaskTemplates } from '../hooks/queries/useTaskTemplates';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as solidIcons from '@fortawesome/free-solid-svg-icons';

const statusOptions = ['Not Started', 'In Progress', 'On Hold', 'Completed'];

function formatTaskLabel(value) {
  return String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
}

const OnboardingTasks = () => {
  const { selected, setSelected } = useContext(SelectedCompanyContext);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notesMap, setNotesMap] = useState({});
  const [noteInputs, setNoteInputs] = useState({});
  const [activeServiceTaskId, setActiveServiceTaskId] = useState(null);
  const [activeSystemTaskId, setActiveSystemTaskId] = useState(null);
  const [mainTab, setMainTab] = useState('services'); // 'services' | 'systems'
  const [savingId, setSavingId] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const { data: templatesData } = useCompanyTaskTemplates(selected?.id);

  // fetch tasks if not included
  useEffect(() => {
    const fetchTasks = async () => {
      if (!selected) return;
      if (selected.CompanyTasks) {
        setTasks(selected.CompanyTasks);
        const map = {};
        selected.CompanyTasks.forEach(t => {
          if (t.CompanyTaskNotes) {
            map[t.id] = t.CompanyTaskNotes;
          }
        });
        setNotesMap(map);
        return;
      }
      setLoading(true);
      try {
        const res = await api.get(`/tasks/company/${selected.id}`);
        setTasks(res.data);
        const map = {};
        res.data.forEach(t => {
          if (t.CompanyTaskNotes) {
            map[t.id] = t.CompanyTaskNotes;
          }
        });
        setNotesMap(map);
        setSelected(prev => ({ ...prev, CompanyTasks: res.data }));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchTasks();
  }, [selected, setSelected]);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const res = await api.get('/auth/verify');
        if (res.data?.valid) setCurrentUser(res.data.user);
      } catch (err) {
        console.error('auth verify', err);
      }
    };
    loadUser();
  }, []);

  const handleStatusChange = async (taskId, newStatus) => {
    setSavingId(taskId);
    try {
      const payload = { status: newStatus };
      if (newStatus !== 'Completed') {
        payload.completionDate = null;
      }
      const res = await api.put(`/tasks/${taskId}`, payload);
      setTasks(prev => prev.map(t => {
        if (t.id !== taskId) return t;
        const merged = { ...t, ...res.data };
        if (!res.data.CompanyTaskNotes) merged.CompanyTaskNotes = t.CompanyTaskNotes;
        return merged;
      }));
      setSelected(prev => ({
        ...prev, CompanyTasks: prev.CompanyTasks.map(t => {
          if (t.id !== taskId) return t;
          const merged = { ...t, ...res.data };
          if (!res.data.CompanyTaskNotes) merged.CompanyTaskNotes = t.CompanyTaskNotes;
          return merged;
        })
      }));
    } catch (err) {
      console.error(err);
      alert('Failed to update');
    } finally {
      setSavingId(null);
    }
  };

  const handleAddNote = async (taskId) => {
    const text = (noteInputs[taskId] || '').trim();
    if (!text) return;
    try {
      const res = await api.post(`/tasks/${taskId}/notes`, { note: text });
      setNotesMap(prev => ({ ...prev, [taskId]: [...(prev[taskId] || []), res.data] }));
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, CompanyTaskNotes: [...(t.CompanyTaskNotes || []), res.data] } : t));
      setSelected(prev => ({ ...prev, CompanyTasks: prev.CompanyTasks.map(t => t.id === taskId ? { ...t, CompanyTaskNotes: [...(t.CompanyTaskNotes || []), res.data] } : t) }));
      setNoteInputs(prev => ({ ...prev, [taskId]: '' }));
    } catch (e) {
      console.error('add note', e);
      alert('Failed to add');
    }
  };

  // Filter tasks
  const templates = (Array.isArray(templatesData) && templatesData.length > 0)
    ? templatesData
    : getFallbackTemplates();

  const templateIconMap = new Map(
    templates
      .filter((t) => t && t.title && t.icon)
      .map((t) => [t.title, t.icon])
  );

  const internalTitleSet = new Set(
    templates
      .filter((t) => t.group === 'internalSystems' && t.isActive !== false)
      .map((t) => t.title)
  );

  const branchServiceTitleSet = new Set(
    templates
      .filter((t) => t.group === 'branchServices' && t.isActive !== false)
      .map((t) => t.title)
  );

  const internalTasksAll = tasks.filter(t => !t.branchId && internalTitleSet.has(t.type));
  // Deduplicate internal systems by type (only one entry per system)
  const internalTasks = Array.from(
    new Map(internalTasksAll.map(t => [t.type, t])).values()
  );
  const companyTasks = tasks.filter(t => !t.branchId && !internalTitleSet.has(t.type) && !branchServiceTitleSet.has(t.type));

  const getTaskIcon = (type) => {
    const iconValue = templateIconMap.get(type);
    if (!iconValue) return '📋';
    if (solidIcons[iconValue]) {
      return <FontAwesomeIcon icon={solidIcons[iconValue]} />;
    }
    return iconValue;
  };

  // Debug logging for mobile
  if (window.Capacitor) {
    console.log('📊 Tasks Debug:', {
      totalTasks: tasks.length,
      internalTasks: internalTasks.length,
      companyTasks: companyTasks.length,
      internalTaskTypes: internalTasks.map(t => t.type),
      companyTaskTypes: companyTasks.map(t => t.type)
    });
  }

  const totalInternalTasks = internalTasks.length;
  const completedInternalTasks = internalTasks.filter(t => t.status === 'Completed').length;
  const totalCompanyTasks = companyTasks.length;
  const completedCompanyTasks = companyTasks.filter(t => t.status === 'Completed').length;

  useEffect(() => {
    if (!companyTasks.length) return;
    const exists = companyTasks.some((t) => t.id === activeServiceTaskId);
    if (!exists) setActiveServiceTaskId(companyTasks[0].id);
  }, [companyTasks, activeServiceTaskId]);

  useEffect(() => {
    if (!internalTasks.length) return;
    const exists = internalTasks.some((t) => t.id === activeSystemTaskId);
    if (!exists) setActiveSystemTaskId(internalTasks[0].id);
  }, [internalTasks, activeSystemTaskId]);

  if (!selected) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center">
        <AnimatedBackground />
        <div className="relative z-10 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <div className="text-gray-900 dark:text-white text-lg">Loading tasks...</div>
        </div>
      </div>
    );
  }

  const isAdmin = currentUser?.role === 'Admin';

  // Render task content with notes
  const renderTaskContent = (task) => (
    <div key={task.id}>
      {/* Task Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <h3 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
          <span className="text-3xl">{getTaskIcon(task.type)}</span>
          {formatTaskLabel(task.type)}
        </h3>
        <div className="flex items-center gap-3">
          {savingId === task.id && (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
          )}
          {(task.status !== 'Completed' || isAdmin) && (
            <ModernSelect
              value={task.status}
              onChange={e => handleStatusChange(task.id, e.target.value)}
              options={statusOptions.map(s => ({ value: s, label: s }))}
              className="w-48"
              disabled={savingId === task.id}
            />
          )}
          <ModernBadge variant={getStatusVariant(task.status)} size="lg">
            {task.status}
          </ModernBadge>
        </div>
      </div>

      {/* Task Details */}
      <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-gray-100 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/10">
        <div>
          <span className="text-gray-600 dark:text-slate-400 text-sm">Completed By</span>
          <p className="text-gray-900 dark:text-white">
            {task.status === 'Completed' && task.CompletedBy
              ? (task.CompletedBy.name || task.CompletedBy.username || task.CompletedBy.id)
              : '—'}
          </p>
        </div>
        <div>
          <span className="text-gray-600 dark:text-slate-400 text-sm">Completion Date</span>
          <p className="text-gray-900 dark:text-white">
            {task.status === 'Completed' && task.completionDate
              ? format(new Date(task.completionDate), 'yyyy-MM-dd HH:mm')
              : '—'}
          </p>
        </div>
      </div>

      {/* Notes Section */}
      {(notesMap[task.id] || []).length > 0 && (
        <div className="space-y-3 mb-6">
          <h4 className="text-lg text-gray-900 dark:text-white font-semibold flex items-center gap-2 mb-3">
            <span>💬</span>
            Activity Log
          </h4>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {(notesMap[task.id] || []).map(n => (
              <div key={n.id} className="bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg p-4 hover:bg-gray-200 dark:hover:bg-white/10 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="text-sm font-medium text-gray-700 dark:text-slate-300">
                    {n.author?.name || n.author?.username || `User ${n.authorId}`}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-slate-500">
                    {new Date(n.createdAt).toLocaleString()}
                  </div>
                </div>
                <div className="text-gray-700 dark:text-slate-300">{n.note}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Note Form */}
      {task.status !== 'Completed' && (
        <div className="space-y-3">
          <h4 className="text-lg text-gray-900 dark:text-white font-semibold flex items-center gap-2">
            <span>✏️</span>
            Add Update
          </h4>
          <div className="flex flex-col lg:flex-row gap-3">
            <ModernTextarea
              value={noteInputs[task.id] || ''}
              onChange={e => setNoteInputs(prev => ({ ...prev, [task.id]: e.target.value }))}
              placeholder="Add a note or update..."
              rows={3}
              className="flex-1"
            />
            <ModernButton
              variant="primary"
              onClick={() => handleAddNote(task.id)}
              className="lg:self-start"
            >
              Add Note
            </ModernButton>
          </div>
        </div>
      )}

      {task.status === 'Completed' && (
        <div className="text-center py-8 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
          <div className="inline-flex items-center gap-3 text-emerald-400">
            <span className="text-4xl">✓</span>
            <span className="text-xl font-semibold">Task Completed</span>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <AnimatedBackground />

      <div className="relative z-10 p-6 lg:p-8 max-w-7xl mx-auto">
        {/* Header */}
        <ModernCard className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">Onboarding Tasks</h1>
          <p className="text-gray-600 dark:text-slate-400">{selected.name}</p>
        </ModernCard>

        {/* Main Tabs: Services vs Systems */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={() => setMainTab('services')}
            className={`
              flex-1 py-4 px-6 rounded-2xl font-semibold transition-all duration-300 flex items-center justify-center gap-3 shadow-sm
              ${mainTab === 'services'
                ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg shadow-blue-500/30 border border-blue-500/30'
                : 'bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-white/10'
              }
            `}
          >
            <span className="text-2xl">📋</span>
            <div className="text-left">
              <div>Company Services</div>
              <div className="text-xs opacity-75">
                {completedCompanyTasks}/{totalCompanyTasks} completed
              </div>
            </div>
          </button>
          
          <button
            onClick={() => setMainTab('systems')}
            className={`
              flex-1 py-4 px-6 rounded-2xl font-semibold transition-all duration-300 flex items-center justify-center gap-3 shadow-sm
              ${mainTab === 'systems'
                ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-500/30 border border-emerald-500/30'
                : 'bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-white/10'
              }
            `}
          >
            <span className="text-2xl">⚙️</span>
            <div className="text-left">
              <div>Internal Systems</div>
              <div className="text-xs opacity-75">
                {completedInternalTasks}/{totalInternalTasks} configured
              </div>
            </div>
          </button>
        </div>

        {/* Company Services Section */}
        {mainTab === 'services' && (
          <>
            {companyTasks.length === 0 ? (
              <ModernCard className="mb-8">
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">📋</div>
                  <div className="text-xl text-slate-400 mb-2">No company services configured.</div>
                  <div className="text-slate-500 text-sm">Services will appear here once they are added during onboarding.</div>
                </div>
              </ModernCard>
            ) : (
              <ModernCard className="mb-8">
                {/* Service Tab Navigation */}
                <div className="mb-6">
                  {/* Mobile: Dropdown/Select */}
                  <div className="md:hidden">
                    <select
                      value={activeServiceTaskId ?? ''}
                      onChange={(e) => setActiveServiceTaskId(Number(e.target.value))}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white font-medium focus:outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20"
                    >
                      {companyTasks.map((task) => (
                        <option key={task.id} value={task.id} className="bg-slate-800">
                          {formatTaskLabel(task.type)} - {task.status}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Desktop: Horizontal Tabs */}
                  <div className="hidden md:flex overflow-x-auto scrollbar-hide bg-white/5 border border-white/10 rounded-xl px-2 py-2 shadow-inner gap-2 snap-x snap-mandatory">
                    {companyTasks.map((task) => {
                      const isActive = activeServiceTaskId === task.id;
                      return (
                        <button
                          key={task.id}
                          onClick={() => setActiveServiceTaskId(task.id)}
                          className={`
                            relative px-4 py-2 font-medium whitespace-nowrap transition-all duration-300
                            max-w-[220px] flex items-center gap-2 rounded-lg border border-transparent snap-start
                            ${isActive 
                              ? 'bg-white/10 text-white border-white/20' 
                              : 'text-slate-300 hover:text-slate-100'
                            }
                          `}
                          title={formatTaskLabel(task.type)}
                        >
                          <span>{getTaskIcon(task.type)}</span>
                          <span className="truncate">{formatTaskLabel(task.type)}</span>
                          <ModernBadge 
                            variant={getStatusVariant(task.status)} 
                            size="sm"
                            className="ml-1"
                          >
                            {task.status === 'Completed' ? 'Done' : task.status}
                          </ModernBadge>
                          
                          {isActive && (
                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500"></div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Service Tab Content */}
                <div className="py-4">
                  {(companyTasks.find((t) => t.id === activeServiceTaskId) || companyTasks[0]) &&
                    renderTaskContent(companyTasks.find((t) => t.id === activeServiceTaskId) || companyTasks[0])}
                </div>
              </ModernCard>
            )}
          </>
        )}

        {/* Internal Systems Section with tabs */}
        {mainTab === 'systems' && (
          <ModernCard className="mt-2">
            {internalTasks.length === 0 ? (
              <div className="text-center py-10">
                <div className="text-5xl mb-3">⚙️</div>
                <div className="text-slate-400 text-lg mb-2">No Internal Systems</div>
                <div className="text-slate-500 text-sm">
                  Internal systems will appear here once configured
                </div>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  {/* Mobile: Dropdown/Select */}
                  <div className="md:hidden">
                    <select
                      value={activeSystemTaskId ?? ''}
                      onChange={(e) => setActiveSystemTaskId(Number(e.target.value))}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white font-medium focus:outline-none focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/20"
                    >
                      {internalTasks.map((task) => (
                        <option key={task.id} value={task.id} className="bg-slate-800">
                          {formatTaskLabel(task.type)} - {task.status}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Desktop: Horizontal Tabs */}
                  <div className="hidden md:flex overflow-x-auto scrollbar-hide bg-white/5 border border-white/10 rounded-xl px-2 py-2 shadow-inner gap-2 snap-x snap-mandatory">
                    {internalTasks.map((task) => {
                      const isActive = activeSystemTaskId === task.id;
                      return (
                        <button
                          key={task.id}
                          onClick={() => setActiveSystemTaskId(task.id)}
                          className={`
                            relative px-4 py-2 font-medium whitespace-nowrap transition-all duration-300
                            max-w-[220px] flex items-center gap-2 rounded-lg border border-transparent snap-start
                            ${isActive 
                              ? 'bg-white/10 text-white border-white/20' 
                              : 'text-slate-400 hover:text-slate-300'
                            }
                          `}
                          title={formatTaskLabel(task.type)}
                        >
                          <span>{getTaskIcon(task.type)}</span>
                          <span className="truncate">{formatTaskLabel(task.type)}</span>
                          <ModernBadge 
                            variant={getStatusVariant(task.status)} 
                            size="sm"
                            className="ml-1"
                          >
                            {task.status === 'Completed' ? 'Done' : task.status}
                          </ModernBadge>
                          
                          {isActive && (
                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-500 to-teal-500"></div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="py-4">
                  {(internalTasks.find((t) => t.id === activeSystemTaskId) || internalTasks[0]) &&
                    renderTaskContent(internalTasks.find((t) => t.id === activeSystemTaskId) || internalTasks[0])}
                </div>
              </>
            )}
          </ModernCard>
        )}
      </div>
    </div>
  );
};

export default OnboardingTasks;
